import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getClient, getTimeRemaining, isTimerExpired, expireClientAccess } from '../../services/clientService';

const STATUS_LABELS = {
  pending_deposit: { text: 'Pending Deposit', color: 'bg-yellow-100 text-yellow-800' },
  active: { text: 'Active', color: 'bg-green-100 text-green-800' },
  in_progress: { text: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  review: { text: 'Under Review', color: 'bg-purple-100 text-purple-800' },
  completed: { text: 'Completed', color: 'bg-green-100 text-green-800' },
  expired: { text: 'Expired', color: 'bg-red-100 text-red-800' },
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Restore session
  const getSession = useCallback(() => {
    try {
      const raw = sessionStorage.getItem('clientSession');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  // Load client data
  useEffect(() => {
    const session = getSession();
    if (!session?.id) {
      navigate('/client/login');
      return;
    }

    const loadClient = async () => {
      try {
        const data = await getClient(session.id);
        if (!data) {
          sessionStorage.removeItem('clientSession');
          navigate('/client/login');
          return;
        }

        // Only expire based on timer if deposit is required and paid
        if (data.requireDeposit && data.depositPaid && isTimerExpired(data.timerExpiresAt)) {
          if (data.projectStatus !== 'expired') {
            await expireClientAccess(data.id);
          }
        }

        setClient(data);
      } catch (err) {
        console.error('Error loading client:', err);
      } finally {
        setLoading(false);
      }
    };

    loadClient();
  }, [navigate, getSession]);

  // Live countdown timer
  useEffect(() => {
    if (!client?.timerExpiresAt) return;

    const tick = () => {
      const remaining = getTimeRemaining(client.timerExpiresAt);
      setTimeRemaining(remaining);

      if (remaining.expired && client.projectStatus !== 'expired') {
        expireClientAccess(client.id);
        setClient(prev => ({ ...prev, projectStatus: 'expired', accessEnabled: false }));
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [client?.timerExpiresAt, client?.id, client?.projectStatus]);

  const handleLogout = () => {
    sessionStorage.removeItem('clientSession');
    navigate('/client/login');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Session Expired</h2>
          <Link to="/client/login" className="text-apn-blue hover:underline">Return to login</Link>
        </div>
      </div>
    );
  }

  const status = STATUS_LABELS[client.projectStatus] || STATUS_LABELS.active;
  const depositRequired = !!client.requireDeposit;
  const depositDone = !!client.depositPaid;
  const needsDeposit = depositRequired && !depositDone;
  // Only apply timer/expiry logic when deposit is required and paid
  const isExpired = depositRequired && depositDone && (client.projectStatus === 'expired' || timeRemaining?.expired);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-blue.png`}
                alt="APN Logo"
                className="h-10"
              />
            </Link>
            <span className="ml-4 text-lg font-semibold text-gray-700">Client Portal</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{client.companyName}</span>
            <button
              onClick={handleLogout}
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Deposit Required CTA */}
        {needsDeposit && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <svg className="h-12 w-12 text-yellow-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-bold text-yellow-800 mb-2">Complete Your $5 Deposit</h3>
            <p className="text-yellow-700 mb-4">
              A $5 refundable deposit is required to activate your 48-hour site preview. This amount is applied toward any plan you choose.
            </p>
            <Link
              to={`/client/deposit/${client.id}`}
              className="inline-flex items-center px-6 py-3 bg-apn-blue text-white rounded-lg hover:bg-apn-dark-blue transition-colors font-medium"
            >
              Pay $5 Deposit & Activate Preview
            </Link>
          </div>
        )}

        {/* Timer Banner — only when deposit required and paid */}
        {depositRequired && depositDone && !isExpired && timeRemaining && (
          <div className={`mb-6 rounded-lg p-4 ${
            timeRemaining.hours < 6 ? 'bg-red-50 border border-red-200' :
            timeRemaining.hours < 24 ? 'bg-yellow-50 border border-yellow-200' :
            'bg-green-50 border border-green-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="h-6 w-6 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Preview Access Time Remaining</span>
              </div>
              <div className="flex space-x-2 font-mono text-2xl font-bold">
                <div className="bg-white rounded px-3 py-1 shadow-sm">
                  <span className="text-gray-900">{String(timeRemaining.hours).padStart(2, '0')}</span>
                  <span className="text-xs text-gray-500 block text-center">HRS</span>
                </div>
                <span className="text-gray-400 self-start pt-1">:</span>
                <div className="bg-white rounded px-3 py-1 shadow-sm">
                  <span className="text-gray-900">{String(timeRemaining.minutes).padStart(2, '0')}</span>
                  <span className="text-xs text-gray-500 block text-center">MIN</span>
                </div>
                <span className="text-gray-400 self-start pt-1">:</span>
                <div className="bg-white rounded px-3 py-1 shadow-sm">
                  <span className="text-gray-900">{String(timeRemaining.seconds).padStart(2, '0')}</span>
                  <span className="text-xs text-gray-500 block text-center">SEC</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expired Banner */}
        {isExpired && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <svg className="h-12 w-12 text-red-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-bold text-red-800 mb-2">Preview Access Expired</h3>
            <p className="text-red-700 mb-4">
              Your 48-hour preview window has ended. Contact our team to extend your access or discuss next steps.
            </p>
            <Link
              to="/about/contact"
              className="inline-flex items-center px-4 py-2 bg-apn-blue text-white rounded-lg hover:bg-apn-dark-blue transition-colors"
            >
              Contact Us to Continue
            </Link>
          </div>
        )}

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Project Status</h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${status.color}`}>
              {status.text}
            </span>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Company</h3>
            <p className="text-lg font-semibold text-gray-900">{client.companyName}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Project Type</h3>
            <p className="text-lg font-semibold text-gray-900 capitalize">
              {client.projectType === 'both' ? 'Website + AI Integration' :
               client.projectType === 'ai_integration' ? 'AI Integration' : 'Website Development'}
            </p>
          </div>
        </div>

        {/* Services Requested */}
        {client.servicesRequested?.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Services Requested</h3>
            <div className="flex flex-wrap gap-2">
              {client.servicesRequested.map((service) => (
                <span key={service} className="px-3 py-1 bg-apn-blue bg-opacity-10 text-apn-blue rounded-full text-sm font-medium">
                  {service}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Site Preview — show when no deposit needed OR deposit paid and not expired */}
        {(client.sitePreviewUrl || client.previewUrl) && !isExpired && !needsDeposit && (
          <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Your Site Preview</h3>
                <p className="text-sm text-gray-500 mt-1">Live preview of your project in progress</p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-4 py-2 bg-apn-blue text-white rounded-lg hover:bg-apn-dark-blue transition-colors text-sm"
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
                <a
                  href={client.sitePreviewUrl || client.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700"
                >
                  Open in New Tab ↗
                </a>
              </div>
            </div>
            {showPreview && (
              <div className="w-full" style={{ height: '70vh' }}>
                <iframe
                  src={client.sitePreviewUrl || client.previewUrl}
                  title="Site Preview"
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                />
              </div>
            )}
          </div>
        )}

        {/* Project Notes */}
        {client.projectNotes && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Project Notes</h3>
            <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
              {client.projectNotes}
            </div>
          </div>
        )}

        {/* Tier Pricing — show when no deposit needed OR deposit paid and not expired */}
        {!isExpired && !needsDeposit && !client.tier && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Your Plan</h3>
            <p className="text-sm text-gray-500 mb-6">
              {depositRequired && depositDone
                ? 'Your $5 deposit is applied toward any plan. Choose the package that fits your business.'
                : 'Select the package that fits your business needs.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: 'starter', name: 'Starter', price: '$79', period: '/mo', features: ['Single-page site', 'Mobile responsive', 'Contact form', 'SEO basics'] },
                { key: 'professional', name: 'Professional', price: '$199', period: '/mo', popular: true, features: ['Up to 5 pages', 'Custom design', 'Google Analytics', 'Blog integration'] },
                { key: 'premium', name: 'Premium', price: '$399', period: '/mo', features: ['Up to 10 pages', 'AI chatbot integration', 'Booking system', 'Priority support'] },
                { key: 'enterprise', name: 'Enterprise', price: '$799', period: '/mo', features: ['Unlimited pages', 'Custom integrations', 'E-commerce', 'Dedicated manager'] },
              ].map((tier) => (
                <div key={tier.key} className={`border rounded-lg p-4 relative ${tier.popular ? 'border-apn-blue ring-2 ring-apn-blue ring-opacity-50' : 'border-gray-200'}`}>
                  {tier.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-apn-blue text-white text-xs font-bold px-3 py-0.5 rounded-full">Most Popular</span>
                  )}
                  <h4 className="text-lg font-bold text-gray-900 mt-1">{tier.name}</h4>
                  <p className="text-2xl font-bold text-apn-blue my-2">{tier.price}<span className="text-sm font-normal text-gray-500">{tier.period}</span></p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start">
                        <svg className="h-4 w-4 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/about/contact"
                    className={`block text-center py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      tier.popular ? 'bg-apn-blue text-white hover:bg-apn-dark-blue' : 'border border-apn-blue text-apn-blue hover:bg-blue-50'
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Tier */}
        {client.tier && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-green-800 mb-1">Your Plan</h3>
            <p className="text-green-700 capitalize text-2xl font-bold">{client.tier}</p>
          </div>
        )}

        {/* Contact CTA */}
        <div className="bg-apn-blue rounded-lg p-8 text-center text-white">
          <h3 className="text-xl font-bold mb-2">Have Questions About Your Project?</h3>
          <p className="text-blue-100 mb-4">Our team is here to help. Reach out anytime.</p>
          <Link
            to="/about/contact"
            className="inline-flex items-center px-6 py-3 bg-white text-apn-blue rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Contact Our Team
          </Link>
        </div>
      </main>
    </div>
  );
}
